import { supabaseFetch } from './core';

// ============================================
// EXERCISE REQUESTS
// ============================================

/**
 * Propone un nuovo esercizio creando una richiesta.
 * Se createPendingTemplate=true, crea anche un template pending che può essere usato in sessione.
 * @returns {{ request, template }} - request sempre presente, template solo se createPendingTemplate=true
 */
export const createExerciseRequest = async ({
  name,
  suggestedMuscleGroup = null,
  note = null,
  userId,
  userRole,
  createPendingTemplate = false
}) => {
  let template = null;

  if (createPendingTemplate) {
    // Crea template pending (non appare nel catalogo)
    const templateData = {
      name,
      primary_muscle_group: suggestedMuscleGroup || null,
      status: 'pending'
    };
    const created = await supabaseFetch('/exercise_templates', {
      method: 'POST',
      body: JSON.stringify(templateData)
    });
    template = Array.isArray(created) ? created[0] : created;
  }

  // Crea la richiesta
  const requestData = {
    requested_name: name,
    requested_by_user_id: userId,
    requested_by_role: userRole,
    suggested_muscle_group: suggestedMuscleGroup || null,
    note: note || null,
    exercise_template_id: template?.id || null,
    status: 'pending'
  };

  const created = await supabaseFetch('/exercise_requests', {
    method: 'POST',
    body: JSON.stringify(requestData)
  });
  const request = Array.isArray(created) ? created[0] : created;

  return { request, template };
};

/**
 * Lista le richieste esercizi (solo admin/coach).
 * @param {string} statusFilter - 'pending' | 'approved' | 'rejected' | null (tutti)
 */
export const getExerciseRequests = async (statusFilter = null) => {
  let endpoint = '/exercise_requests?select=*&order=created_at.desc';
  if (statusFilter) {
    endpoint += `&status=eq.${statusFilter}`;
  }

  const requests = await supabaseFetch(endpoint);
  if (!requests || requests.length === 0) return [];

  // Arricchisci con nome utente richiedente
  const userIds = [...new Set(requests.map(r => r.requested_by_user_id).filter(Boolean))];
  let profiles = [];
  if (userIds.length > 0) {
    profiles = await supabaseFetch(`/profiles?id=in.(${userIds.join(',')})&select=id,name,email`);
  }

  return requests.map(r => ({
    ...r,
    requesterName: profiles.find(p => p.id === r.requested_by_user_id)?.name || 'Sconosciuto',
    requesterEmail: profiles.find(p => p.id === r.requested_by_user_id)?.email || ''
  }));
};

/**
 * Approva una richiesta: aggiorna il template con i dati completi e lo rende 'active'.
 * Se non esiste ancora un template pending, ne crea uno nuovo.
 */
export const approveExerciseRequest = async (requestId, templateData, adminId) => {
  // Leggi la richiesta
  const requests = await supabaseFetch(`/exercise_requests?id=eq.${requestId}&select=*`);
  if (!requests || requests.length === 0) throw new Error('Richiesta non trovata');
  const request = requests[0];

  let templateId = request.exercise_template_id;

  if (templateId) {
    // Aggiorna il template esistente (pending → active)
    await supabaseFetch(`/exercise_templates?id=eq.${templateId}`, {
      method: 'PATCH',
      body: JSON.stringify({ ...templateData, status: 'active' })
    });
  } else {
    // Crea un nuovo template
    const created = await supabaseFetch('/exercise_templates', {
      method: 'POST',
      body: JSON.stringify({ ...templateData, status: 'active' })
    });
    const newTemplate = Array.isArray(created) ? created[0] : created;
    templateId = newTemplate.id;
  }

  // Aggiorna la richiesta come approvata
  await supabaseFetch(`/exercise_requests?id=eq.${requestId}`, {
    method: 'PATCH',
    body: JSON.stringify({
      status: 'approved',
      exercise_template_id: templateId,
      reviewed_by_admin_id: adminId,
      reviewed_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    })
  });

  return templateId;
};

/**
 * Rifiuta una richiesta. Se esiste un template pending associato, lo marca come rejected.
 */
export const rejectExerciseRequest = async (requestId, adminId) => {
  const requests = await supabaseFetch(`/exercise_requests?id=eq.${requestId}&select=*`);
  if (!requests || requests.length === 0) throw new Error('Richiesta non trovata');
  const request = requests[0];

  // Marca il template pending come rejected (se esiste)
  if (request.exercise_template_id) {
    await supabaseFetch(`/exercise_templates?id=eq.${request.exercise_template_id}`, {
      method: 'PATCH',
      body: JSON.stringify({ status: 'rejected' })
    });
  }

  // Aggiorna la richiesta
  await supabaseFetch(`/exercise_requests?id=eq.${requestId}`, {
    method: 'PATCH',
    body: JSON.stringify({
      status: 'rejected',
      reviewed_by_admin_id: adminId,
      reviewed_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    })
  });
};
